import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { getSelectedOrderId } from 'entities/Order/model/selectors/ordersSelectors';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';

import {
    applyOrderSelectionQuantityLocal,
    syncOrderSelectionQuantity,
} from './applyOrderSelectionQuantity';

import type { SyncOrderSelectionQuantityParams } from './applyOrderSelectionQuantity';

const SYNC_DEBOUNCE_MS = 500;

interface ApplyOrderSelectionQuantityArgs {
    selectionKey: string;
    itemUid: string;
    nextQty: number;
    orderLineId?: string;
    syncToOrder?: boolean;
}

export const useApplyOrderSelectionQuantity = () => {
    const dispatch = useAppDispatch();
    const selectedOrderId = useSelector(getSelectedOrderId);
    const [syncingKeys, setSyncingKeys] = useState<Record<string, true>>({});
    const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const syncGenerationRef = useRef<Record<string, number>>({});

    const clearTimer = useCallback((selectionKey: string) => {
        const timerId = timersRef.current[selectionKey];
        if (timerId) {
            clearTimeout(timerId);
            delete timersRef.current[selectionKey];
        }
    }, []);

    const setKeySyncing = useCallback(
        (selectionKey: string, isSyncing: boolean) => {
            setSyncingKeys((current) => {
                if (isSyncing) {
                    if (current[selectionKey]) {
                        return current;
                    }
                    return { ...current, [selectionKey]: true };
                }
                if (!current[selectionKey]) {
                    return current;
                }
                const next = { ...current };
                delete next[selectionKey];
                return next;
            });
        },
        [],
    );

    const runSync = useCallback(
        async (
            selectionKey: string,
            params: SyncOrderSelectionQuantityParams,
        ) => {
            const generation =
                (syncGenerationRef.current[selectionKey] ?? 0) + 1;
            syncGenerationRef.current[selectionKey] = generation;
            setKeySyncing(selectionKey, true);

            try {
                await syncOrderSelectionQuantity(params);
            } finally {
                if (syncGenerationRef.current[selectionKey] === generation) {
                    setKeySyncing(selectionKey, false);
                }
            }
        },
        [setKeySyncing],
    );

    useEffect(
        () => () => {
            Object.values(timersRef.current).forEach((timerId) => {
                clearTimeout(timerId);
            });
        },
        [],
    );

    const apply = useCallback(
        async ({
            selectionKey,
            itemUid,
            nextQty,
            orderLineId,
            syncToOrder = false,
        }: ApplyOrderSelectionQuantityArgs) => {
            applyOrderSelectionQuantityLocal({
                dispatch,
                selectionKey,
                itemUid,
                nextQty,
            });

            if (!syncToOrder || !selectedOrderId) {
                return;
            }

            const syncParams = {
                dispatch,
                selectedOrderId,
                orderLineId: orderLineId ?? selectionKey,
                nextQty,
            };

            clearTimer(selectionKey);

            if (nextQty < 1) {
                await runSync(selectionKey, syncParams);
                return;
            }

            timersRef.current[selectionKey] = setTimeout(() => {
                delete timersRef.current[selectionKey];
                void runSync(selectionKey, syncParams);
            }, SYNC_DEBOUNCE_MS);
        },
        [clearTimer, dispatch, runSync, selectedOrderId],
    );

    return { apply, syncingKeys };
};
